/**
 * VISALL 缓存管理器
 * 基于数据和配置指纹的智能缓存系统，集成到现有生命周期
 */

import type { Data, Spec, CacheEntry, CacheClearTrigger } from './types';

export class CacheManager {
  // 全局缓存：跨图表实例，存储可复用的验证结果
  private static globalCache = new Map<string, CacheEntry>();

  // 实例缓存：绑定到api.cache，随图表实例生命周期管理
  private instanceCache?: Map<string, unknown>;

  // 缓存配置
  private config = {
    // 全局缓存最大条目数
    maxSize: 100,
    // TTL: 5分钟
    ttl: 5 * 60 * 1000,
    // 是否启用实例缓存
    enableInstance: true,
    // 是否启用全局缓存
    enableGlobal: true
  };

  constructor(options?: {
    maxSize?: number;
    ttl?: number;
    enableInstance?: boolean;
    enableGlobal?: boolean;
  }) {
    if (options) {
      this.config = { ...this.config, ...options };
    }
  }

  /**
   * 绑定到API实例缓存
   */
  bindToApiCache(apiCache: Map<string, unknown>): void {
    this.instanceCache = apiCache;
  }

  /**
   * 🔑 生成数据和配置的指纹
   * 核心：基于数据结构+配置的唯一标识
   */
  generateFingerprint(data: Data, spec: Spec): string {
    try {
      // 检查是否启用全局缓存（确保this被使用）
      if (!this.config.enableGlobal && !this.config.enableInstance) {
        return `disabled-${Date.now()}-${Math.random()}`;
      }

      // 1. 数据指纹：基于数据结构和样本
      const dataFingerprint = CacheManager.generateDataFingerprint(data);

      // 2. 配置指纹：基于图层配置
      const specFingerprint = CacheManager.generateSpecFingerprint(spec);

      // 3. 组合指纹
      return `validation-${dataFingerprint}-${specFingerprint}`;
    } catch (error) {
      console.error('Error generating cache fingerprint:', error);
      // 返回时间戳作为fallback，确保不会缓存
      return `fallback-${Date.now()}-${Math.random()}`;
    }
  }

  /**
   * 生成数据指纹
   */
  private static generateDataFingerprint(data: Data): string {
    const fingerprints: string[] = [];

    data.forEach((dataset, index) => {
      const { values } = dataset;

      if (!values) {
        fingerprints.push(`dataset-${index}-null`);
        return;
      }

      // 🆕 支持不同数据结构类型
      if (Array.isArray(values)) {
        // 表格数据处理
        if (values.length === 0) {
          fingerprints.push(`dataset-${index}-array-empty`);
          return;
        }

        const fields = Object.keys(values[0]).sort();
        const rowCount = values.length;

        // 样本数据指纹（取前3行和后1行）
        const sampleRows = [
          ...values.slice(0, Math.min(3, values.length)),
          ...(values.length > 3 ? [values[values.length - 1]] : [])
        ];

        const sampleHash = CacheManager.hashObject(sampleRows);
        fingerprints.push(`dataset-${index}-array-${fields.join(',')}-${rowCount}-${sampleHash}`);
      } else if (typeof values === 'object') {
        // 层次数据处理（如树图）
        const objectKeys = Object.keys(values).sort();
        const sampleHash = CacheManager.hashObject(values);
        fingerprints.push(`dataset-${index}-object-${objectKeys.join(',')}-${sampleHash}`);
      } else {
        // 其他类型数据
        const dataType = typeof values;
        const sampleHash = CacheManager.hashObject(values);
        fingerprints.push(`dataset-${index}-${dataType}-${sampleHash}`);
      }
    });

    return fingerprints.join('|');
  }

  /**
   * 生成配置指纹
   */
  private static generateSpecFingerprint(spec: Spec): string {
    const layerFingerprints = spec.layers.map(layer => {
      // 关键配置：类型+编码+数据索引
      const keyConfig = {
        type: layer.type,
        encoding: layer.encoding || {},
        dataIndex: layer.dataIndex || 0
      };

      return CacheManager.hashObject(keyConfig);
    });

    return layerFingerprints.join('-');
  }

  /**
   * 对象哈希计算
   */
  private static hashObject(obj: unknown): string {
    const str = JSON.stringify(obj, Object.keys(obj as object).sort());
    return CacheManager.simpleHash(str);
  }

  /**
   * 简单哈希函数
   */
  private static simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      // 转换为32位整数
      hash &= hash;
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * 获取缓存
   */
  get(key: string): CacheEntry | null {
    // 1. 优先从实例缓存获取
    if (this.config.enableInstance && this.instanceCache?.has(key)) {
      const cached = this.instanceCache.get(key) as CacheEntry;
      if (!this.shouldClearCache({ type: 'ttl-expired', timestamp: cached.timestamp })) {
        return cached;
      }
      // TTL过期，清除
      this.instanceCache.delete(key);
    }

    // 2. 从全局缓存获取
    if (this.config.enableGlobal && CacheManager.globalCache.has(key)) {
      const cached = CacheManager.globalCache.get(key)!;
      if (!this.shouldClearCache({ type: 'ttl-expired', timestamp: cached.timestamp })) {
        // 复制到实例缓存
        if (this.config.enableInstance && this.instanceCache) {
          this.instanceCache.set(key, cached);
        }
        return cached;
      }
      // TTL过期，清除
      CacheManager.globalCache.delete(key);
    }

    return null;
  }

  /**
   * 设置缓存
   */
  set(key: string, entry: CacheEntry): void {
    // 内存压力检查
    if (this.shouldClearCache({ type: 'memory-pressure' })) {
      this.performMemoryPressureCleanup();
    }

    // 1. 设置到实例缓存
    if (this.config.enableInstance && this.instanceCache) {
      this.instanceCache.set(key, entry);
    }

    // 2. 设置到全局缓存
    if (this.config.enableGlobal) {
      CacheManager.globalCache.set(key, entry);
    }
  }

  /**
   * 🎯 缓存清除决策
   * 核心：智能判断何时清除缓存
   */
  shouldClearCache(trigger: CacheClearTrigger): boolean {
    switch (trigger.type) {
      case 'chart-destroy':
        // 图表销毁时，清除实例缓存，保留全局缓存
        return trigger.scope === 'instance';

      case 'data-change':
        // 数据变化时，清除相关缓存
        return trigger.oldFingerprint !== trigger.newFingerprint;

      case 'spec-change':
        // 配置变化时，清除验证缓存
        return trigger.oldFingerprint !== trigger.newFingerprint;

      case 'ttl-expired':
        // TTL过期检查
        if (!trigger.timestamp) {
          return false;
        }
        return Date.now() - trigger.timestamp > this.config.ttl;

      case 'memory-pressure':
        // 内存压力检查
        return CacheManager.globalCache.size > this.config.maxSize;

      default:
        return false;
    }
  }

  /**
   * 内存压力清理
   */
  private performMemoryPressureCleanup(): void {
    if (!this.config.enableGlobal) {
      return;
    }

    const entries = Array.from(CacheManager.globalCache.entries());

    // 按时间戳排序，清除最老的25%
    entries.sort(([, a], [, b]) => a.timestamp - b.timestamp);

    const removeCount = Math.floor(entries.length * 0.25);
    const toRemove = entries.slice(0, removeCount);

    toRemove.forEach(([key]) => {
      CacheManager.globalCache.delete(key);
    });

    console.log(`CacheManager: Memory pressure cleanup, removed ${removeCount} entries`);
  }

  /**
   * 🔄 集成到现有生命周期的静态方法
   */
  static setupLifecycleIntegration(): void {
    // 这个方法会在第3步中被调用，集成到api.js的destroy和update方法
    console.log('CacheManager lifecycle integration setup completed');
  }

  /**
   * 清除特定实例的缓存
   */
  static clearInstanceCache(visId: string): void {
    if (!CacheManager.globalCache.size) {
      return;
    }

    let clearedCount = 0;
    const keys = Array.from(CacheManager.globalCache.keys());

    keys.forEach(key => {
      // 简单的visId匹配策略
      if (key.includes(visId) || key.includes('validation-')) {
        CacheManager.globalCache.delete(key);
        clearedCount++;
      }
    });

    if (clearedCount > 0) {
      console.log(`CacheManager: Cleared ${clearedCount} cache entries for instance ${visId}`);
    }
  }

  /**
   * 判断是否需要在update时清除缓存
   */
  shouldClearOnUpdate(oldSpec: Spec, newSpec: Spec): boolean {
    try {
      // 生成新旧配置的指纹
      const oldFingerprint = CacheManager.generateSpecFingerprint(oldSpec);
      const newFingerprint = CacheManager.generateSpecFingerprint(newSpec);

      return this.shouldClearCache({
        type: 'spec-change',
        oldFingerprint,
        newFingerprint
      });
    } catch (error) {
      console.error('Error comparing spec fingerprints:', error);
      // 出错时保守地清除缓存
      return true;
    }
  }

  /**
   * 获取缓存统计信息
   */
  getCacheStats() {
    const globalSize = CacheManager.globalCache.size;
    const instanceSize = this.instanceCache?.size || 0;

    // 计算过期条目数量
    const now = Date.now();
    let expiredCount = 0;

    CacheManager.globalCache.forEach(entry => {
      if (now - entry.timestamp > this.config.ttl) {
        expiredCount++;
      }
    });

    return {
      global: {
        size: globalSize,
        maxSize: this.config.maxSize,
        utilization: globalSize / this.config.maxSize,
        expiredCount
      },
      instance: {
        size: instanceSize,
        bound: !!this.instanceCache
      },
      config: this.config
    };
  }

  /**
   * 手动清除所有缓存（用于调试）
   */
  static clearAllCache(): void {
    CacheManager.globalCache.clear();
    console.log('CacheManager: All cache cleared manually');
  }

  /**
   * 清除过期的缓存条目
   */
  cleanupExpiredEntries(): number {
    const now = Date.now();
    let cleanedCount = 0;

    // 清理全局缓存中的过期条目
    CacheManager.globalCache.forEach((entry, key) => {
      if (now - entry.timestamp > this.config.ttl) {
        CacheManager.globalCache.delete(key);
        cleanedCount++;
      }
    });

    // 清理实例缓存中的过期条目
    if (this.instanceCache) {
      const keys = Array.from(this.instanceCache.keys());
      keys.forEach(key => {
        if (key.startsWith('validation-')) {
          const entry = this.instanceCache!.get(key) as CacheEntry;
          if (entry && now - entry.timestamp > this.config.ttl) {
            this.instanceCache!.delete(key);
            cleanedCount++;
          }
        }
      });
    }

    if (cleanedCount > 0) {
      console.log(`CacheManager: Cleaned up ${cleanedCount} expired cache entries`);
    }

    return cleanedCount;
  }
}
